import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Paperclip, ArrowUp, X, Mic, Pencil, Sparkles } from "lucide-react";
import { NeuralMapLayer } from "./NeuralMapLayer";
import { GuidedIntake } from "./GuidedIntake";
import { OpportunityExpansion } from "./OpportunityExpansion";
import { ContextualQuestions } from "./ContextualQuestions";
import { interpret, readiness, detectLang, getOpportunities, sufficiency } from "../lib/thinking";
import { uploadFile, createCase, refineMap, assessCase } from "../lib/api";

export function BriefingExperience({ t, lang, seed = "", forceContact = false, onDone }) {
  const [text, setText] = useState(seed);
  const [phase, setPhase] = useState(forceContact ? "intake" : "writing");
  const [guideIdx, setGuideIdx] = useState(-1);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [listening, setListening] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [aiMap, setAiMap] = useState(null);
  const [refining, setRefining] = useState(false);
  const [intakeValues, setIntakeValues] = useState(null);
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [assessing, setAssessing] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [contextualAnswers, setContextualAnswers] = useState([]);
  const [gptOpps, setGptOpps] = useState(null);
  const [speechSupported] = useState(
    () => typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition)
  );

  const taRef = useRef(null);
  const fileRef = useRef(null);
  const dragDepth = useRef(0);
  const recognitionRef = useRef(null);
  const voiceBaseRef = useRef("");

  const state = readiness(text);
  const clientMap = useMemo(() => interpret(text, lang, state), [text, lang, state]);
  const interpretation = aiMap || clientMap;
  const opportunities = useMemo(() => {
    if (gptOpps && gptOpps.length) return gptOpps.map((o, i) => ({ key: `g${i}`, title: o.title, body: o.body }));
    return getOpportunities(interpretation, lang);
  }, [gptOpps, interpretation, lang]);

  const handleRefine = async () => {
    if (state === "short" || refining) return;
    setRefining(true);
    try {
      const data = await refineMap(text, lang);
      const domains = (data.domains || []).slice(0, 4).map((d, i) => ({
        key: `ai${i}`,
        area: d.area || "",
        label: d.node || "",
        branches: (d.sub || []).slice(0, 7).map((s) => ({ label: s })),
      }));
      setAiMap({ core: { label: data.core || clientMap.core?.label || "" }, areas: data.areas || [], domains });
    } catch (err) {
      console.error(err);
    } finally {
      setRefining(false);
    }
  };

  const updateText = (v) => {
    setText(v);
    if (aiMap) setAiMap(null);
    if (gptOpps) setGptOpps(null);
    if (questions.length) setQuestions([]);
  };

  useEffect(() => {
    const el = taRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 300) + "px";
    }
  }, [text]);

  useEffect(() => {
    if (phase === "writing") taRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (state !== "short" && guideIdx >= 0) setGuideIdx(-1);
  }, [state, guideIdx]);

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch (_) {}
    };
  }, []);

  const handleAdvance = async () => {
    if (state === "short") {
      setGuideIdx((i) => (i + 1) % t.brief.guidance.length);
      taRef.current?.focus();
      return;
    }
    try {
      recognitionRef.current?.stop();
    } catch (_) {}
    setAssessing(true);
    try {
      const data = await assessCase(text, lang);
      if (data.core || (data.domains && data.domains.length)) {
        const domains = (data.domains || []).slice(0, 4).map((d, i) => ({
          key: `ai${i}`,
          area: d.area || "",
          label: d.node || "",
          branches: (d.sub || []).slice(0, 7).map((s) => ({ label: s })),
        }));
        setAiMap({ core: { label: data.core || clientMap.core?.label || "" }, areas: data.areas || [], domains });
      }
      if (data.opportunities && data.opportunities.length) setGptOpps(data.opportunities);
      const qs = (data.questions || []).slice(0, 3);
      setQuestions(qs);
      setPhase(qs.length ? "questions" : "intake");
    } catch (err) {
      console.error(err);
      // graceful local fallback — never block the flow
      const suff = sufficiency(text, interpretation);
      if (suff !== "SUFFICIENT") {
        const qs = t.brief.guidance.slice(0, 2);
        setQuestions(qs);
        setPhase("questions");
      } else {
        setQuestions([]);
        setPhase("intake");
      }
    } finally {
      setAssessing(false);
    }
  };

  const handleQuestionsDone = (answers) => {
    setContextualAnswers(answers || []);
    setPhase("intake");
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const ref = await uploadFile(f);
        setAttachments((p) => [...p, ref]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleFiles = (e) => uploadFiles(e.target.files);
  const removeAttachment = (id) => setAttachments((p) => p.filter((a) => a.id !== id));

  const onDragEnter = (e) => {
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const rec = new SR();
    rec.lang = detectLang(text, lang) === "pt" ? "pt-BR" : "en-US";
    rec.continuous = true;
    rec.interimResults = true;
    voiceBaseRef.current = text ? text.trim() + " " : "";
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = 0; i < e.results.length; i++) transcript += e.results[i][0].transcript;
      setText(voiceBaseRef.current + transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch (_) {}
  };

  const handleIntakeDone = (values) => {
    setIntakeValues(values);
    setPhase("opportunity");
  };

  const toggleOpp = (key) =>
    setSelectedOpps((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const handleFinalSubmit = async () => {
    const values = intakeValues || {};
    setError("");
    setSending(true);
    const problem_map = {
      core: interpretation.core?.label || "",
      areas: interpretation.areas,
      domains: interpretation.domains.map((d) => ({
        area: d.area,
        node: d.label,
        sub: d.branches.map((b) => b.label),
      })),
    };
    const contactAns = [values.email, values.whatsapp].filter(Boolean).join(" / ");
    const transcript = [
      { q: t.brief.placeholder, a: text },
      ...contextualAnswers,
      { q: t.intake.q.company, a: values.company },
      { q: t.intake.q.industry, a: values.industry },
      { q: t.intake.q.location, a: values.location },
      { q: t.intake.q.website, a: values.website },
      { q: t.intake.q.name, a: values.name },
      { q: t.intake.q.role, a: values.role },
      { q: t.intake.q.contact, a: contactAns },
      { q: t.intake.q.notes, a: values.notes },
    ].filter((x) => (x.a || "").trim());
    const selected = opportunities.filter((o) => selectedOpps.includes(o.key)).map((o) => o.title);
    try {
      const res = await createCase({
        problem_original: text,
        transcript,
        problem_map,
        areas: interpretation.areas,
        opportunity_suggestions: opportunities.map((o) => o.title),
        selected_opportunities: selected,
        client: {
          company: values.company || null,
          industry: values.industry || null,
          website: values.website || null,
          name: values.name || null,
          role: values.role || null,
          location: values.location || null,
          email: values.email || null,
          whatsapp: values.whatsapp || null,
          notes: values.notes || null,
        },
        language: lang,
      });
      setConfirming(true);
      setTimeout(() => onDone(res.case_id, interpretation), 950);
    } catch (err) {
      console.error(err);
      setError(t.contact.error);
      setSending(false);
    }
  };

  const preview = text.trim().length > 90 ? text.trim().slice(0, 90) + "…" : text.trim();

  return (
    <div
      data-testid="briefing-experience"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className="relative min-h-screen flex flex-col items-center justify-center px-6 md:px-12 py-24"
    >
      <NeuralMapLayer interpretation={interpretation} />

      <AnimatePresence>
        {isDragging && (
          <motion.div
            data-testid="drop-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none absolute inset-4 md:inset-8 z-30 flex items-center justify-center border border-dashed border-bh-c2 bg-bh-bg/85"
          >
            <span className="font-display text-xl md:text-2xl tracking-tight text-bh-soft">{t.brief.drop}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirming && (
          <motion.div
            data-testid="send-pulse"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center"
          >
            <motion.span
              className="block h-3 w-3 rounded-full bg-bh-ink"
              initial={{ scale: 0.6, opacity: 0.9 }}
              animate={{ scale: [0.6, 1, 26], opacity: [0.9, 0.7, 0] }}
              transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: confirming ? 0 : 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-3xl mx-auto text-center"
      >
        <AnimatePresence mode="wait">
          {phase === "writing" ? (
            <motion.div key="writing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">{t.brief.headline}</h2>
              <p className="mt-3 text-sm md:text-base text-bh-soft">{t.brief.support}</p>

              <div className="mt-8 border-b border-bh-line pb-4">
                <textarea
                  data-testid="briefing-field"
                  ref={taRef}
                  rows={1}
                  value={text}
                  onChange={(e) => updateText(e.target.value)}
                  placeholder={t.brief.placeholder}
                  className="no-scrollbar w-full resize-none bg-transparent outline-none text-center text-2xl md:text-[2rem] leading-snug tracking-tight placeholder:text-bh-c2"
                />
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <button
                    data-testid="attach-btn"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 text-bh-c3 hover:text-bh-ink transition-colors"
                    title={t.brief.attach}
                  >
                    <Paperclip size={16} strokeWidth={1.5} />
                    <span className="text-xs tracking-wide">{t.brief.attach}</span>
                  </button>

                  {speechSupported && (
                    <button
                      data-testid="voice-btn"
                      onClick={toggleVoice}
                      className={`flex items-center gap-2 transition-colors ${listening ? "text-bh-ink" : "text-bh-c3 hover:text-bh-ink"}`}
                      title={listening ? t.brief.listening : t.brief.voice}
                      aria-pressed={listening}
                    >
                      <span className="relative flex items-center">
                        <Mic size={16} strokeWidth={1.5} />
                        {listening && (
                          <motion.span
                            className="absolute -right-2 -top-1 h-1.5 w-1.5 rounded-full bg-bh-ink"
                            animate={{ opacity: [1, 0.2, 1], scale: [1, 0.7, 1] }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                          />
                        )}
                      </span>
                      <span className="text-xs tracking-wide" data-testid="voice-label">
                        {listening ? t.brief.listening : t.brief.voice}
                      </span>
                    </button>
                  )}

                  {uploading && (
                    <span data-testid="uploading-indicator" className="flex items-center gap-1.5 text-xs text-bh-c2">
                      <motion.span
                        className="inline-block h-1 w-1 rounded-full bg-bh-c2"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                      />
                      {t.brief.uploading}
                    </span>
                  )}

                  {state !== "short" && (
                    <button
                      data-testid="refine-ai-btn"
                      onClick={handleRefine}
                      disabled={refining}
                      className={`flex items-center gap-2 transition-colors ${aiMap ? "text-bh-ink" : "text-bh-c3 hover:text-bh-ink"} disabled:opacity-60`}
                      title={t.brief.refine}
                    >
                      {refining ? (
                        <motion.span
                          className="inline-block h-3.5 w-3.5 rounded-full border border-bh-c3 border-t-transparent"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <Sparkles size={15} strokeWidth={1.5} />
                      )}
                      <span className="text-xs tracking-wide" data-testid="refine-label">
                        {refining ? t.brief.refining : aiMap ? t.brief.refined : t.brief.refine}
                      </span>
                    </button>
                  )}

                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.csv"
                    onChange={handleFiles}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>

                <button
                  data-testid="advance-btn"
                  onClick={handleAdvance}
                  disabled={assessing}
                  className="group flex h-11 w-11 items-center justify-center rounded-full border border-bh-line hover:border-bh-ink transition-colors disabled:opacity-50"
                  aria-label={t.brief.continue}
                >
                  {assessing ? (
                    <motion.span
                      data-testid="advance-loader"
                      className="inline-block h-4 w-4 rounded-full border border-bh-c3 border-t-transparent"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
                    />
                  ) : (
                    <ArrowUp size={18} strokeWidth={1.5} className="transition-transform duration-500 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </div>

              {attachments.length > 0 && (
                <div className="mt-4 flex flex-wrap justify-start gap-3">
                  <AnimatePresence>
                    {attachments.map((a) => (
                      <motion.span
                        key={a.id}
                        data-testid="attachment-chip"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="inline-flex items-center gap-2 text-xs text-bh-soft border-b border-bh-line pb-0.5"
                      >
                        {a.original_filename}
                        <button onClick={() => removeAttachment(a.id)} className="hover:text-bh-ink">
                          <X size={12} />
                        </button>
                      </motion.span>
                    ))}
                  </AnimatePresence>
                </div>
              )}

              <AnimatePresence>
                {guideIdx >= 0 && state === "short" && (
                  <motion.p
                    data-testid="short-guidance"
                    key={guideIdx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mt-6 text-sm text-bh-c3"
                  >
                    {t.brief.guidance[guideIdx]}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          ) : phase === "questions" ? (
            <motion.div key="questions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <ContextualQuestions t={t} questions={questions} onDone={handleQuestionsDone} />
            </motion.div>
          ) : phase === "intake" ? (
            <motion.div key="intake" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              {preview && (
                <button
                  data-testid="brief-preview"
                  onClick={() => setPhase("writing")}
                  className="group mx-auto mb-12 flex max-w-lg items-start gap-2 text-left text-sm text-bh-c3 hover:text-bh-soft transition-colors"
                >
                  <Pencil size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <span className="italic">“{preview}”</span>
                </button>
              )}
              <GuidedIntake t={t} onSubmit={handleIntakeDone} sending={sending} error={error} />
            </motion.div>
          ) : (
            <motion.div key="opportunity" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <OpportunityExpansion
                t={t}
                items={opportunities}
                selected={selectedOpps}
                toggle={toggleOpp}
                onContinue={handleFinalSubmit}
                sending={sending}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
